import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Card, CardContent, CardHeader, Grid, Input, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import axios from "axios";
import { createForm } from "rc-form";
import * as React from "react";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";
import { UserArmy } from "./types/playerTypes";

interface PlayerArmyState {
    loaded: boolean;
    capos: any | null;
    followersConfig: any[];
    artilleryConfig: any[];
    levelFormVisible: boolean;
    activeLevelForm: string | null;
    army: UserArmy;
}

interface PlayerArmyProps {
    playerId: string;
    activeEnv: any;
    playerData: any;
    heroesConfig: any;
    form: any;
}

class PlayerArmyMain extends React.Component<PlayerArmyProps, PlayerArmyState> {

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            capos: null,
            army: null as any,
            // Configuration
            followersConfig: [],
            artilleryConfig: [],
            // Inline form editor
            levelFormVisible: false,
            activeLevelForm: null,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    fetchUserCapos = () => axios.get(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/capos`, {
        headers: { Authorization: Helpers.getSessionId(), env: this.props.activeEnv },
    })

    fetchUserArmy = () => axios.get(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/army`, {
        headers: { Authorization: Helpers.getSessionId(), env: this.props.activeEnv },
    })

    fetchUnitsConfig = () => axios.get(`${Config.getInstance.apiUrl}/api/units`, {
        headers: { Authorization: Helpers.getSessionId() },
    })

    fetchData = () =>
        axios.all([
            this.fetchUnitsConfig(),
            this.fetchUserCapos(),
            this.fetchUserArmy(),
        ]).then(axios.spread((
            unitsConfig,
            userCapos,
            userArmy,
        ) => {
            this.setState({
                loaded: true,
                followersConfig: unitsConfig.data.data.followers.map(unit => {
                    unit.name = Helpers.getLocalizationName(unit.name);

                    return unit;
                }),
                artilleryConfig: unitsConfig.data.data.artillery.map(unit => {
                    unit.name = Helpers.getLocalizationName(unit.name);

                    return unit;
                }),
                capos: userCapos.data.data,
                army: userArmy.data.data,
            });
        })).catch((_err) => {
            PopupMessage.error("Something went wrong");
        })

    refreshList = () => {
        this.setState({ loaded: false }, () => {
            this.fetchData();
        });
    }

    isCapoAssigned = (capo) => capo.altar === null ? <CancelIcon
        style={{ color: "red", fontSize: 18 }}
    /> : <CheckCircleOutlineIcon
        style={{ color: "green", fontSize: 18 }}
    />;

    getCapoNameById = (capoId) => {
        const capoConfig = this.props.heroesConfig.find(capo => +capo.id === capoId);
        return capoConfig ? `${Helpers.getLocalizationName(capoConfig.name)} [${capoId}]` : capoId;
    }

    getFollowerNameById = (followerId) => {
        const followerConfig = this.state.followersConfig.find(follower => +follower.id === followerId);
        return followerConfig ? `${Helpers.getLocalizationName(followerConfig.name)} [${followerId}]` : followerId;
    }

    getArtilleryNameById = (artilleryId) => {
        const artilleryConfig = this.state.artilleryConfig.find(artillery => +artillery.id === artilleryId);
        return artilleryConfig ? `${Helpers.getLocalizationName(artilleryConfig.name)} [${artilleryId}]` : artilleryId;
    }

    openForm = (levelId) => this.setState({ levelFormVisible: true, activeLevelForm: levelId });

    closeForm = () => this.setState({ levelFormVisible: false, activeLevelForm: null });

    levelFormId = (type, data) => {
        if (type === "capo") {
            return `capo_${data.hero_id}`;
        }

        if (type === "artillery") {
            return `artillery_${data.id}`;
        }

        if (type === "follower") {
            return `follower_${data.id}`;
        }

        return null;
    }

    handleKeyPress = (e) => {
        if (e.key === "Enter") {
            this.handleLevelChange();
        }
    }

    handleLevelChange = () => {
        this.props.form.validateFields((err, values) => {
            if (!err) {
                if (!this.state.activeLevelForm) {
                    return;
                }

                const activeElement = this.state.activeLevelForm.split("_");
                const type = activeElement[0];
                const id = parseInt(activeElement[1], 10);

                if (type === "capo") {
                    axios.put(`${Config.getInstance.apiUrl}/api/capo/level`, {
                        ...values,
                        level: parseInt(values.level, 10),
                        userId: this.props.playerId,
                        id,
                    }, {
                        headers: {
                            Authorization: Helpers.getSessionId(),
                            env: this.props.activeEnv,
                        },
                    })
                    .then(data => {
                        const tempCapos = { ...this.state.capos };
                        const capoItem = tempCapos.heroes.find(hero => hero.hero_id === id);
                        capoItem.level = values.level;

                        return this.setState({
                            capos: tempCapos,
                            levelFormVisible: false,
                            activeLevelForm: null,
                        });
                    })
                    .catch(() => {
                        PopupMessage.error("Something went wrong!");
                    });
                }

                if (type === "artillery" || type === "follower") {
                    axios.put(`${Config.getInstance.apiUrl}/api/army/level`, {
                        ...values,
                        level: parseInt(values.level, 10),
                        userId: this.props.playerId,
                        id,
                        type,
                    }, {
                        headers: {
                            Authorization: Helpers.getSessionId(),
                            env: this.props.activeEnv,
                        },
                    })
                    .then(data => {
                        const tempArmy = { ...this.state.army };

                        if (type === "artillery") {
                            const followerItem = tempArmy.unlockedArtillery.find(follower => follower.id === id);

                            if (followerItem) {
                                followerItem.level = values.level;
                            }
                        }

                        if (type === "follower") {
                            const followerItem = (tempArmy?.unlockedFollowers || []).find(follower => follower.id === id);

                            if (followerItem) {
                                followerItem.level = values.level;
                            }
                        }

                        return this.setState({
                            army: tempArmy,
                            levelFormVisible: false,
                            activeLevelForm: null,
                        });
                    })
                    .catch(() => {
                        PopupMessage.error("Something went wrong!");
                    });
                }
            }
        });
    }

    levelForm = (_type, data) => {
        const { getFieldDecorator } = this.props.form;

        const formButtonStyle = {
            display: "inline-block",
            fontSize: 20,
            marginLeft: 5,
        };

        const successButtonStyle = {
            ...formButtonStyle,
            color: "green",
        };

        const cancelButtonStyle = {
            ...formButtonStyle,
        };

        return <React.Fragment>
            <div style={{ display: "inline-block" }}>
                {getFieldDecorator("level", {
                    initialValue: data.level,
                })(
                    <Input autoFocus onKeyDown={this.handleKeyPress} style={{ width: 60 }}/>,
                )}
            </div>
            <div style={{position: "absolute", left: 76, top: 7 }}>
                <div style={successButtonStyle}>
                    <CheckCircleOutlineIcon onClick={() => this.handleLevelChange()}/>
                </div>
                <div style={cancelButtonStyle}>
                    <CancelIcon onClick={() => this.closeForm()}/>
                </div>
            </div>
        </React.Fragment>
    }

    toggleLevelInlineForm = (type, data) => {
        const levelId = this.levelFormId(type, data);

        if (this.state.levelFormVisible && levelId === this.state.activeLevelForm) {
            return this.levelForm(type, data);
        }

        if (this.props.playerData.is_online) {
            return data.level;
        }

        return <span onClick={() => this.openForm(levelId)} className="edit--field-unactive">
            {data.level}
        </span>;
    }

    renderArtilleryRows() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={2}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if ((this.state?.army?.unlockedArtillery || []).length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={3}>
                    <Alert severity="info">No Artillery</Alert>
                </TableCell>
            </TableRow>
        }

        return (this.state?.army?.unlockedArtillery || []).map(item => (
            <TableRow key={item.id}>
                <TableCell>{this.getArtilleryNameById(item.id)}</TableCell>
                <TableCell style={{ position: "relative" }}>
                    {this.toggleLevelInlineForm("artillery", item)}
                </TableCell>
            </TableRow>
        ));
    }

    renderFollowersRows() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={2}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if ((this.state?.army?.unlockedFollowers || []).length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={3}>
                    <Alert severity="info">No Followers</Alert>
                </TableCell>
            </TableRow>
        }

        return (this.state?.army?.unlockedFollowers || []).map(item => (
            <TableRow key={item.id}>
                <TableCell>{this.getFollowerNameById(item.id)}</TableCell>
                <TableCell style={{ position: "relative" }}>
                    {this.toggleLevelInlineForm("follower", item)}
                </TableCell>
            </TableRow>
        ));
    }

    renderHeroesRows() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={3}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>
        }

        if (this.state.capos.heroes.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={3}>
                    <Alert severity="info">No Heroes</Alert>
                </TableCell>
            </TableRow>
        }

        return this.state.capos.heroes.map(item => (
            <TableRow key={item.hero_id}>
                <TableCell>{this.getCapoNameById(item.hero_id)}</TableCell>
                <TableCell style={{ position: "relative" }}>
                    {this.toggleLevelInlineForm("capo", item)}
                </TableCell>
                <TableCell>{this.isCapoAssigned(item)}</TableCell>
            </TableRow>
        ));
    }

    render() {
        return <Grid container spacing={3}>
            <Grid item xs={4}>
                <Card elevation={0}>
                    <CardHeader title="Heroes"/>
                    <CardContent>
                        <Table size="small">
                           <TableHead>
                               <TableRow>
                                   <TableCell>Capo</TableCell>
                                   <TableCell>Level</TableCell>
                                   <TableCell>Assigned to altar</TableCell>
                               </TableRow>
                           </TableHead>
                           <TableBody>
                               {this.renderHeroesRows()}
                           </TableBody>
                       </Table>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={4}>
                <Card elevation={0}>
                    <CardHeader title="Troops"/>
                    <CardContent>
                        <Table size="small">
                           <TableHead>
                               <TableRow>
                                   <TableCell>Follower</TableCell>
                                   <TableCell>Level</TableCell>
                               </TableRow>
                           </TableHead>
                           <TableBody>
                               {this.renderFollowersRows()}
                           </TableBody>
                       </Table>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={4}>
                <Card elevation={0}>
                    <CardHeader title="Artillery"/>
                    <CardContent>
                        <Table size="small">
                           <TableHead>
                               <TableRow>
                                   <TableCell>Follower</TableCell>
                                   <TableCell>Level</TableCell>
                               </TableRow>
                           </TableHead>
                           <TableBody>
                               {this.renderArtilleryRows()}
                           </TableBody>
                       </Table>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>;
    }
}

export const PlayerArmy = createForm()(PlayerArmyMain);
